export type PcsoGameId = '6-58' | '6-55' | '6-49' | '6-45' | '6-42';
export type PcsoJackpotStatus = 'official' | 'estimated' | 'unavailable';

export type PcsoLastDraw = {
  date: string | null;
  drawTime: string | null;
  winningNumbers: string[];
  jackpot: number | null;
  jackpotFormatted: string | null;
  jackpotWinners: number | null;
};

export type PcsoGame = {
  id: PcsoGameId;
  code: string;
  name: string;
  jackpot: number | null;
  jackpotFormatted: string | null;
  jackpotStatus: PcsoJackpotStatus;
  jackpotSource: string | null;
  nextDraw: string | null;
  drawTime: string;
  scheduleDays: number[];
  lastDraw: PcsoLastDraw;
  sourceUrl: string;
  lastVerified: string | null;
};

export type PcsoResponse = {
  lastUpdated: string | null;
  timezone: 'Asia/Manila';
  sourceName: string;
  sourceUrl: string;
  scheduleSourceUrl: string;
  stale: boolean;
  warning: string | null;
  games: PcsoGame[];
};

export const PCSO_SCHEDULES: Array<{ id: PcsoGameId; code: string; name: string; maxNumber: number; days: number[] }> = [
  { id: '6-58', code: 'UL58', name: 'Ultra Lotto 6/58', maxNumber: 58, days: [0, 2, 5] },
  { id: '6-55', code: 'GL55', name: 'Grand Lotto 6/55', maxNumber: 55, days: [1, 3, 6] },
  { id: '6-49', code: 'SL49', name: 'Super Lotto 6/49', maxNumber: 49, days: [0, 2, 4] },
  { id: '6-45', code: 'ML45', name: 'Mega Lotto 6/45', maxNumber: 45, days: [1, 3, 5] },
  { id: '6-42', code: 'LOTTO42', name: 'Lotto 6/42', maxNumber: 42, days: [2, 4, 6] },
];

export type PcsoCalendarEvent = {
  id: string;
  title: string;
  startUtc: string;
  endUtc: string;
  zoneId: 'philippines';
  color: string;
  description: string;
  kind: 'event';
  work: 'PCSO Lotto';
  readOnly: true;
  pcsoGameId: PcsoGameId;
};

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addUtcDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return next;
}

function easterSundayUtc(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day, 12));
}

function isStandardNoDrawDate(date: Date): boolean {
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  if ((month === 0 && day === 1) || (month === 11 && day === 25)) return true;
  const easter = easterSundayUtc(date.getUTCFullYear());
  const daysBeforeEaster = Math.round((easter.getTime() - date.getTime()) / 86_400_000);
  return daysBeforeEaster >= 0 && daysBeforeEaster <= 3;
}

export function generatePcsoCalendarEvents(startDate: string, endDate: string, games: PcsoGame[] = []): PcsoCalendarEvent[] {
  const start = new Date(startDate + 'T12:00:00Z');
  const end = new Date(endDate + 'T12:00:00Z');
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start > end) return [];
  const gameMap = new Map(games.map((game) => [game.id, game]));
  const events: PcsoCalendarEvent[] = [];
  for (let date = start; date <= end; date = addUtcDays(date, 1)) {
    if (isStandardNoDrawDate(date)) continue;
    const dateValue = isoDate(date);
    PCSO_SCHEDULES.forEach((schedule) => {
      if (!schedule.days.includes(date.getUTCDay())) return;
      const game = gameMap.get(schedule.id);
      const isOfficialNextDraw = game?.nextDraw?.slice(0, 10) === dateValue;
      const jackpotText = isOfficialNextDraw && game?.jackpotFormatted
        ? ' · Official advertised jackpot ' + game.jackpotFormatted
        : '';
      events.push({
        id: 'pcso-' + schedule.id + '-' + dateValue,
        title: schedule.name,
        startUtc: dateValue + 'T13:00:00.000Z',
        endUtc: dateValue + 'T13:30:00.000Z',
        zoneId: 'philippines',
        color: '#d7a06f',
        description: 'Official PCSO draw schedule · 9:00 PM Asia/Manila' + jackpotText,
        kind: 'event',
        work: 'PCSO Lotto',
        readOnly: true,
        pcsoGameId: schedule.id,
      });
    });
  }
  return events;
}

export async function fetchPcsoData(force = false): Promise<PcsoResponse> {
  const response = await fetch('/api/pcso' + (force ? '?refresh=1' : ''), { cache: 'no-store' });
  if (!response.ok) throw new Error('PCSO information is unavailable (' + response.status + ').');
  const data = await response.json() as PcsoResponse;
  if (!data || data.timezone !== 'Asia/Manila' || !Array.isArray(data.games)) throw new Error('PCSO returned an invalid response.');
  return data;
}

export function readBooleanPreference(key: string, fallback: boolean): boolean {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
}

export function readNumberPreference(key: string, fallback: number): number {
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch {
    return fallback;
  }
}

export function savePreference(key: string, value: string | number | boolean): void {
  try { window.localStorage.setItem(key, String(value)); } catch { /* Preferences remain optional. */ }
}
